import { Controller } from "@hotwired/stimulus"
import { anchorPanel } from "#lib/floating_panel"
import getCaretCoordinates from "textarea-caret"

// [[ ]] wiki-reference autocomplete over a textarea (the Marksmith editor's
// field). Watches for "[[", shows a dropdown of entities (filtered by what you
// type after "[["), and inserts [[slug]] on pick (link text resolves to the
// entity name at render — never stale). Entity data comes from a peer
// entity-list controller (outlet) — this controller owns only the UI.
//
// Attach both controllers to a wrapper around the editor; the picker finds the
// <textarea> inside its own element (we don't control Marksmith's inner markup):
//   <div data-controller="entity-list entity-picker"
//        data-entity-picker-entity-list-outlet="[data-controller~=entity-list]">
//     … marksmith editor (contains a <textarea>) …
//   </div>
export default class extends Controller {
  static outlets = ["entity-list"]

  connect() {
    this.menu = null
    this.matches = []
    this.activeIndex = 0
    this.triggerPos = null // index of the "[[" start, or null when inactive

    this.input =
      this.element.querySelector("textarea.marksmith-textarea") ||
      this.element.querySelector("textarea")
    if (!this.input) return

    this.onInput = this.onInput.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onBlur = this.onBlur.bind(this)
    this.input.addEventListener("input", this.onInput)
    this.input.addEventListener("keydown", this.onKeydown)
    this.input.addEventListener("blur", this.onBlur)
  }

  disconnect() {
    if (this.input) {
      this.input.removeEventListener("input", this.onInput)
      this.input.removeEventListener("keydown", this.onKeydown)
      this.input.removeEventListener("blur", this.onBlur)
    }
    this.closeMenu()
  }

  // --- detection ----------------------------------------------------------

  onInput() {
    const query = this.activeQuery()
    if (query === null) return this.closeMenu()
    this.showMatches(query)
  }

  // If the cursor sits inside an open "[[…" (no closing "]]" or whitespace-only
  // run after it), return the query text after "[["; otherwise null.
  activeQuery() {
    const el = this.input
    const upToCursor = el.value.slice(0, el.selectionStart)
    const open = upToCursor.lastIndexOf("[[")
    if (open === -1) return null

    const between = upToCursor.slice(open + 2)
    // Bail if the ref was already closed, or a newline broke it.
    if (between.includes("]]") || between.includes("\n")) return null

    this.triggerPos = open
    return between
  }

  // --- menu ---------------------------------------------------------------

  showMatches(query) {
    const list = this.hasEntityListOutlet ? this.entityListOutlet : null
    if (!list) return this.closeMenu()

    this.matches = list.search(query)
    if (this.matches.length === 0) return this.closeMenu()

    this.activeIndex = 0
    this.renderMenu()
  }

  renderMenu() {
    // Remove the previous menu element only — do NOT call closeMenu(), which
    // also resets this.matches (the data we're about to render).
    if (this.menu) {
      this.menu.remove()
      this.menu = null
    }
    // Outer menu wrapper holds the arrow and is NOT overflow-clipped; the inner
    // <ul> carries the scroll. (An overflow container would clip the arrow, which
    // sits just outside its edge.)
    const menu = document.createElement("div")
    menu.className = "floating-panel"

    const list = document.createElement("ul")
    list.className = "entity-picker-list"
    list.setAttribute("role", "listbox")

    this.matches.forEach((entity, i) => {
      const item = document.createElement("li")
      item.className = "entity-picker-item"
      item.setAttribute("role", "option")
      if (i === this.activeIndex) item.classList.add("is-active")
      item.innerHTML =
        `<span class="entity-picker-name"></span>` +
        `<span class="entity-picker-type"></span>`
      item.querySelector(".entity-picker-name").textContent = entity.name
      item.querySelector(".entity-picker-type").textContent = entity.type
      // mousedown (not click) so it fires before the textarea's blur.
      item.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this.pick(i)
      })
      list.appendChild(item)
    })
    menu.appendChild(list)

    // Arrow (the little triangle pointing at the caret), positioned by Floating UI.
    const arrowEl = document.createElement("div")
    arrowEl.className = "floating-panel-arrow"
    menu.appendChild(arrowEl)
    this.arrowEl = arrowEl

    // Floating UI's required floating-element setup (per docs): position pinned to
    // origin + width:max-content so the element isn't resized by a container
    // (which would corrupt the measured size). We use the `fixed` strategy
    // (matched in computePosition) to break out of the editor's clipping.
    menu.style.position = "fixed"
    menu.style.top = "0"
    menu.style.left = "0"
    menu.style.width = "max-content"
    menu.style.minWidth = "16rem"
    menu.style.zIndex = "1000"
    document.body.appendChild(menu)
    this.menu = menu
    // Anchor to a virtual reference at the caret; prefer ABOVE it (top-start),
    // flip below only when there's no room. Shared chrome positioning.
    const reference = { getBoundingClientRect: () => this.caretRect() }
    this.cleanupPosition = anchorPanel(reference, this.menu, this.arrowEl, { placement: "top-start" })
  }

  // Viewport rect of the text caret. textarea-caret (the battle-tested mirror
  // implementation) gives the caret's {top, left, height} relative to the
  // textarea's border box; we offset by the textarea's viewport position and its
  // internal scroll to get viewport coords for Floating UI.
  caretRect() {
    const ta = this.input
    const taRect = ta.getBoundingClientRect()
    const caret = getCaretCoordinates(ta, ta.selectionStart)
    const left = taRect.left + caret.left - ta.scrollLeft
    const top = taRect.top + caret.top - ta.scrollTop
    return DOMRect.fromRect({ x: left, y: top, width: 0, height: caret.height })
  }

  closeMenu() {
    if (this.cleanupPosition) {
      this.cleanupPosition()
      this.cleanupPosition = null
    }
    if (this.menu) {
      this.menu.remove()
      this.menu = null
    }
    this.matches = []
    this.triggerPos = null
  }

  // --- keyboard -----------------------------------------------------------

  onKeydown(e) {
    if (!this.menu) return
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        e.preventDefault()
        this.move(-1)
        break
      case "Enter":
      case "Tab":
        e.preventDefault()
        this.pick(this.activeIndex)
        break
      case "Escape":
        e.preventDefault()
        this.closeMenu()
        break
    }
  }

  move(delta) {
    this.activeIndex =
      (this.activeIndex + delta + this.matches.length) % this.matches.length
    const items = this.menu.querySelectorAll(".entity-picker-item")
    items.forEach((el, i) => el.classList.toggle("is-active", i === this.activeIndex))
    // Keep the highlighted item in view as you arrow through a long list.
    items[this.activeIndex]?.scrollIntoView({ block: "nearest" })
  }

  onBlur() {
    // Defer so a menu mousedown-pick can run first.
    setTimeout(() => this.closeMenu(), 100)
  }

  // --- insertion ----------------------------------------------------------

  pick(index) {
    const entity = this.matches[index]
    if (!entity || this.triggerPos === null) return this.closeMenu()

    const el = this.input
    // Insert the bare [[slug]] — link text resolves to the entity's current name
    // at render time, so it never goes stale. A custom [[slug|Text]] override is
    // still supported (the renderer honors it); the user adds |Text by hand.
    const ref = `[[${entity.slug}]]`
    // Replace from the "[[" trigger to the current cursor with the full ref.
    el.setRangeText(ref, this.triggerPos, el.selectionStart, "end")
    this.closeMenu()
    // Notify Marksmith/listeners that the value changed.
    el.dispatchEvent(new Event("input", { bubbles: true }))
    el.focus()
  }
}
